import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconAlertCircle, IconCheck, IconPhoto } from '@tabler/icons-react';
import './styles.scss';

// A paged image list from a host service: `fetchPage(cursor) → { items, next }`. `key`
// names what is listed (a tab, a category, a search); a new key starts over and an answer
// for an older one is dropped. `prepend` puts images added meanwhile (an upload) first.
export function useImagePages(fetchPage, key) {
  const fetcher = useRef(fetchPage);
  fetcher.current = fetchPage;
  const request = useRef(0);
  const [state, setState] = useState({ status: 'loading', items: [], next: null });
  const load = useCallback(async (cursor = null) => {
    const id = ++request.current;
    if (!fetcher.current) { setState({ status: 'ready', items: [], next: null }); return; }
    setState(previous => (cursor ? { ...previous, status: 'more' } : { status: 'loading', items: [], next: null }));
    try {
      const page = await fetcher.current(cursor);
      if (id !== request.current) return;
      setState(previous => {
        const kept = cursor ? previous.items : [];
        const known = new Set(kept.map(item => item.id));
        return { status: 'ready', items: [...kept, ...(page.items || []).filter(item => !known.has(item.id))], next: page.next || null };
      });
    } catch {
      if (id === request.current) setState(previous => ({ ...previous, status: 'error' }));
    }
  }, []);
  useEffect(() => { load(); }, [key, load]);
  useEffect(() => () => { request.current += 1; }, []);
  const prepend = useCallback(added => setState(previous => ({ ...previous,
    items: [...added, ...previous.items.filter(item => !added.some(image => image.id === item.id))] })), []);
  const { next } = state;
  const more = useCallback(() => { if (next) load(next); }, [next, load]);
  return { ...state, reload: load, more, prepend };
}

// Image tiles for a gallery (the creator's uploads, generated images, a community
// library). A tile shows `thumbnail` when there is one (else `url`); the description is
// its alt text and tooltip. `selectedId` rings the picked one. The grid owns the list's
// states: skeletons while loading, the error with a retry, the empty line and "load more".
// `tileAction(image)` adds a small control over a tile (the editor's "use as background").
// Without `selectedId` the tiles are plain actions; with it (null included) they toggle.
export default function ImageGrid({ items, status = 'ready', next = null, selectedId, onPick, onMore, onRetry, empty, error, label,
  columns = 3, disabled = false, pickLabel, tileAction, skeletons = 6 }) {
  const { t } = useTranslation('artEditor');
  const loading = status === 'loading' && !items.length;
  return <div className={`art-image-grid is-${columns}`}>
    {status === 'error' && <div className="art-inline-error" role="alert">
      <Text.caption contrast={8} left={<IconAlertCircle />}>{error || t('galleryError')}</Text.caption>
      {onRetry && <Button size="small" variant="text" color="overlay" preventLoading onClick={onRetry}>{t('retry')}</Button>}
    </div>}
    {loading && <div className="art-image-grid__tiles" aria-hidden="true">
      {Array.from({ length: skeletons }, (_, index) => <span key={index} className="art-image-grid__tile art-skeleton" />)}
    </div>}
    {status !== 'error' && !loading && !items.length && <div className="art-empty art-image-grid__empty">
      <IconPhoto aria-hidden="true" /><Text.caption contrast={6}>{empty}</Text.caption>
    </div>}
    {items.length > 0 && <div className="art-image-grid__tiles" role="group" aria-label={label} aria-busy={status === 'loading' || undefined}>
      {items.map(image => {
        const on = selectedId != null && image.id === selectedId;
        const description = image.description || '';
        return <div key={image.id} className={`art-image-grid__item ${on ? 'is-selected' : ''}`}>
          <button type="button" className="art-image-grid__tile" disabled={disabled} aria-pressed={selectedId === undefined ? undefined : on}
            aria-label={[pickLabel, description].filter(Boolean).join(': ') || undefined} title={description || undefined} onClick={() => onPick(image)}>
            <img src={image.thumbnail || image.url} alt={description} loading="lazy" draggable={false} />
            {on && <span className="art-image-grid__check" aria-hidden="true"><IconCheck /></span>}
          </button>
          {tileAction?.(image)}
        </div>;
      })}
    </div>}
    {next && onMore && status !== 'error' && <Button className="art-image-grid__more" size="small" variant="text" color="overlay" preventLoading
      loading={status === 'more'} disabled={status === 'more' || status === 'loading'} onClick={onMore}>{t('loadMore')}</Button>}
  </div>;
}
